import { CaretLeftFilled } from '@ant-design/icons'
import { useNavigate, useSearchParams } from '@remix-run/react'
import { Button, Flex, Typography } from 'antd'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useEffect, useState } from 'react'
import { Api } from '~/core/trpc'
import { ErrorMessage } from '../../components/ErrorMessage'
import { SocialButtons } from '../../components/SocialButtons'
import { StepEmail } from '../../components/StepEmail'
import { StepLoading } from '../../components/StepLoading'
import { StepPasswordForgot } from '../../components/StepPasswordForgot'
import { StepPasswordForgotToken } from '../../components/StepPasswordForgotToken'
import { StepSignin } from '../../components/StepSignin'
import { StepSignup } from '../../components/StepSignup'

type StepKey =
  | 'loading'
  | 'email'
  | 'signin'
  | 'signup'
  | 'password-forgot'
  | 'password-forgot-callback'

export const FeatureFlowClassic: React.FC = () => {
  const navigate = useNavigate()

  const [isReady, setReady] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  const stepSaved = searchParams.get('step') as StepKey
  const stepDefault = stepSaved ?? 'email'

  const [step, setStep] = useState<StepKey>(stepDefault)
  const [email, setEmail] = useState<string>()
  const [token, setToken] = useState<string>()

  const { data: providers } = Api.authentication.findManyProviders.useQuery(
    undefined,
    { initialData: [] },
  )

  const handleChangeStep = (step: StepKey, email?: string) => {
    searchParams.set('step', step)
    if (email) {
      searchParams.set('email', email)
    } else {
      searchParams.delete('email')
    }

    setStep(step)
    setEmail(email)
    setSearchParams(searchParams)
  }

  const handleError = (errorKey?: string) => {
    if (errorKey) {
      searchParams.set('error', errorKey)
    } else {
      searchParams.delete('error')
    }

    setSearchParams(searchParams)
  }

  useEffect(() => {
    const email = searchParams.get('email')
    const token = searchParams.get('token')

    if (email) {
      setEmail(email)
    } else if (import.meta.env.DEV) {
      setEmail('test@test.com')
    }

    setToken(token)

    setReady(true)

    return () => {
      searchParams.delete('error')
      searchParams.delete('step')
      searchParams.delete('email')
      searchParams.delete('token')
      setSearchParams(searchParams)
    }
  }, [])

  useEffect(() => {
    if (stepDefault !== step) {
      setStep(stepDefault)
    }
  }, [stepDefault])

  const BackButton = () => {
    return (
      <Flex justify="left" className="mt-4">
        <Button
          type="text"
          size="small"
          icon={<CaretLeftFilled />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </Flex>
    )
  }

  const renderStep = () => {
    switch (step) {
      case 'loading':
        return <StepLoading />
      case 'email':
        return (
          <>
            <StepEmail
              email={email}
              onNext={handleChangeStep}
              onError={handleError}
            />
          </>
        )
      case 'signin':
        return (
          <>
            {email && (
              <>
                <StepSignin
                  email={email}
                  onError={handleError}
                  onPasswordForgot={() =>
                    handleChangeStep('password-forgot', email)
                  }
                />

                <BackButton />
              </>
            )}
          </>
        )
      case 'signup':
        return (
          <>
            {email && (
              <>
                <StepSignup email={email} token={token} onError={handleError} />

                {!token && <BackButton />}
              </>
            )}
          </>
        )
      case 'password-forgot':
        return (
          <>
            {email && (
              <>
                <StepPasswordForgot email={email} onError={handleError} />

                <BackButton />
              </>
            )}
          </>
        )
      case 'password-forgot-callback':
        return (
          <>
            {email && (
              <>
                <StepPasswordForgotToken
                  token={token}
                  onError={handleError}
                  onNext={handleChangeStep}
                />
              </>
            )}
          </>
        )
      default:
        return null
    }
  }

  return (
    <>
      <Flex
        vertical
        className="w-full p-4"
        style={{ maxWidth: '400px', minHeight: '400px' }}
      >
        <Flex vertical className="mb-4" align="center">
          {/* <Typography.Title level={4}>Login</Typography.Title> */}

          <Typography.Title level={3}>Access your account</Typography.Title>
        </Flex>

        <SocialButtons providers={providers} />

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: isReady ? 0.1 : 0 }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>

        <ErrorMessage />
      </Flex>
    </>
  )
}
